'use client';

import React from 'react';
import useSWR from 'swr';
import { usePathname } from 'next/navigation';
import { getRequiredPermissionForPath, hasPermission, MemberPermissions } from '@/lib/permissions';
import { AccessRestricted } from './AccessRestricted';
import { Loader2 } from 'lucide-react';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

type MembershipResponse = {
  role: string;
  teamId: number;
  permissions: MemberPermissions;
};

export function PermissionGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { data: membership, isLoading } = useSWR<MembershipResponse>('/api/team/membership', fetcher, {
    revalidateOnFocus: true,
    revalidateOnMount: true,
  });

  const cleanPath = pathname ? pathname.replace(/^\/[a-z]{2}(?=\/)/, '') : '';

  // Owner-only route: /monitoring
  if (cleanPath === '/monitoring' || cleanPath.startsWith('/monitoring/')) {
    if (isLoading && !membership) {
      return (
        <div className="flex-1 h-full flex items-center justify-center bg-background/50">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground font-medium">Verifying owner access...</p>
          </div>
        </div>
      );
    }
    if (membership?.role !== 'owner') {
      return (
        <AccessRestricted
          role={membership?.role || 'agent'}
          featureName="Team Live Monitoring"
          resource={'settings' as any}
        />
      );
    }
    return <>{children}</>;
  }

  const required = getRequiredPermissionForPath(pathname);

  // If this route does not require any specific permission, render children directly
  if (!required) {
    return <>{children}</>;
  }

  // While membership is loading, show a clean indicator rather than flashing restricted content
  if (isLoading && !membership) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-background/50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground font-medium">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  // Owner always has access
  if (membership?.role === 'owner') {
    return <>{children}</>;
  }

  // Check if member has permission for this resource
  const isAllowed = membership
    ? hasPermission(membership.role, membership.permissions, required.resource)
    : false;

  if (!isAllowed) {
    return (
      <AccessRestricted
        role={membership?.role || 'agent'}
        featureName={required.label}
        resource={required.resource}
      />
    );
  }

  return <>{children}</>;
}
